import { Box, FormControl, FormErrorMessage, Spinner, Stack, Text } from '@chakra-ui/react'
import type { InsuranceParamsTravelDto, Item } from 'BrokerTsLib/src/dtos'
import { Insurer, Selection, SelectionType } from 'BrokerTsLib/src/enums'
import { useFormContext } from 'BrokerTsLib/src/rhf'
import { Select } from 'chakra-react-select'

import { FormLabel } from '../../components/FormLabel'
import { RadioIcon } from '../../components/RadioIcon'
import { RadioIconGroup } from '../../components/RadioIconGroup'
import { RadioStack } from '../../components/RadioStack'
import { useInsurers, useSelections } from '../../hooks'

export const DestinationSelector = () => {
  const { control, getValues, setValue, watch, formState: { errors } } = useFormContext<InsuranceParamsTravelDto>()
  const { data: dataDestination, isLoading: isLoadingDestination, isFetching: isFetchingDestination } = useSelections(SelectionType.TravelDestination)
  const { data: dataEuropeDestination, isLoading: isLoadingEuropeDestination, isFetching: isFetchingEuropeDestination } = useSelections(SelectionType.TravelDestEuropeCountry)

  const { data: allInsurersData } = useInsurers(false, null)

  const isContracted = !!getValues('generalParams.contractNumber')

  const destinationId = watch('travel.destination')

  const destinationEuCountryId = getValues('travel.destinationEuropeCountry')

  let sortedEuDestinations: Item[] = []
  if (dataEuropeDestination && !isLoadingEuropeDestination && !isFetchingEuropeDestination) {
    sortedEuDestinations = dataEuropeDestination
    sortedEuDestinations.sort((a, b) => a.name.localeCompare(b.name))
  }

  let defaultDestination = null
  if (destinationEuCountryId && dataEuropeDestination && !isLoadingEuropeDestination && !isFetchingEuropeDestination) {
    const destData = dataEuropeDestination.find(item => item.id == destinationEuCountryId)
    if (destData) {
      defaultDestination = {
        label: destData.name,
        value: destData.id
      }
    }
  }

  return (
    <>
      <FormControl isRequired isInvalid={!!errors.travel?.destination}>
        <Stack direction={'row'}>
          <FormLabel tooltip={
            <Box>
              <Text>Vyberte destinaci klientova pobytu</Text>
              {allInsurersData && allInsurersData.findIndex(ins => (ins.id == Insurer.Allianz || ins.id == Insurer.Colonnade)) >= 0 && (
                <><br /><Text><strong>UPOZORNĚNÍ:</strong></Text></>
              )}
              {allInsurersData && allInsurersData.findIndex(ins => ins.id == Insurer.Colonnade) >= 0 && (
                <><br /><Text><strong>Pojišťovna Colonnade</strong> řadí země jako Mexiko, státy v Karibské oblasti, Grónsko a vybrané další země do stejné kategorie jako USA. V případě, že klient cestuje do těchto zemí vyberte "Svět" nebo si ověřte aktuální informace v pojišťovně.</Text></>
              )}
              {allInsurersData && allInsurersData.findIndex(ins => ins.id == Insurer.Allianz) >= 0 && (
                <><br /><Text><strong>Pojišťovna Allianz</strong> řadí země jako Americká Samoa, Americké Panenské ostrovy, Guam, Johnstonův atol, Midwayské ostrovy, Serranilla, Severní Mariány, Ostrov Wake a vybrané další země do stejné kategorie jako USA. V případě, že klient cestuje do těchto zemí vyberte "Svět" nebo si ověřte aktuální informace v pojišťovně.</Text></>
              )}
            </Box>
          } >Destinace</FormLabel>
          {(isFetchingDestination || isLoadingDestination) && <Spinner />}
        </Stack>


        <RadioIconGroup control={control} name="travel.destination" isDisabled={isContracted}>
          <RadioStack
            sx={{ gridTemplateColumns: { base: 'repeat(2, minmax(10rem, 1fr))', lg: 'repeat(4, minmax(10rem, 1fr))' } }}
            labelsx={{ fontWeight: 'medium' }}
            inputlabelwrappersx={{
              display: 'grid',
              alignItems: 'center'
            }}>

            {dataDestination?.map((item) => {
              let icon
              switch (item.id) {
                case Selection.TravelDestinationEurope:
                  icon = 'europe'
                  break

                case Selection.TravelDestinationWorldWithoutNorthAmerica:
                  icon = 'world-no-us-can'
                  break

                case Selection.TravelDestinationWorld:
                  icon = 'world'
                  break

                case Selection.TravelDestinationCzech:
                  icon = 'cs'
                  break
              }

              return (
                <RadioIcon value={item.id.toString()} key={item.id} icon={icon} iconSize="48px">
                  {item.description}
                </RadioIcon>
              )
            })}
          </RadioStack>
        </RadioIconGroup>

        {errors.travel?.destination && <FormErrorMessage>{errors.travel.destination.message}</FormErrorMessage>}
      </FormControl>

      {
        destinationId == Selection.TravelDestinationEurope && (
          <FormControl isRequired isInvalid={!!errors.travel?.destinationEuropeCountry}>
            <Stack direction={'row'}>
              <FormLabel tooltip='Vyberte destinaci klientova pobytu. V případě, že bude klient cestovat po více zemích, vyberte tu nejvzdálenější.'>Cílová destinace</FormLabel>
              {(isFetchingEuropeDestination || isLoadingEuropeDestination) && <Spinner />}
            </Stack>

            {dataEuropeDestination && !isLoadingEuropeDestination && !isFetchingEuropeDestination &&
              <Select
                name="travel.destinationEuropeCountry"
                placeholder="Vyberte zemi..."
                isDisabled={isContracted}
                options={sortedEuDestinations.map((country) => ({
                  label: country.name,
                  value: country.id
                }))}
                chakraStyles={{
                  menu: (provided) => ({ ...provided, zIndex: 3 }),
                }}
                onChange={(value) => {
                  setValue('travel.destinationEuropeCountry', value?.value)
                }}
                defaultValue={defaultDestination}
              />
            }

            {errors.travel?.destinationEuropeCountry && <FormErrorMessage>{errors.travel.destinationEuropeCountry.message}</FormErrorMessage>}
          </FormControl>
        )
      }
    </>
  )
}